/**
 * THE columns the order tables and the hires Table BOTH show — one definition,
 * imported by `components/OrderTableView.js`'s `ORDER_TABLE_COLUMNS` and by
 * `components/hires/HireTableView.js`'s `HIRE_TABLE_COLUMNS`.
 *
 * Owner, 2026-09-28, after the Dept Hires Table had drifted from Dept Orders
 * (it lost the PO/Order cycling header among other things): share what can be
 * shared so the two cannot diverge again.
 *
 * 🔑 THE INVARIANT: **a column both tables show is built HERE, never written
 * out as a literal on either side.** What a column IS — its key, its heading,
 * its row-key `sortField`, its cycle `fields`, its measured `min`, its `fitFloor`
 * — comes from this file. What legitimately differs per page is a PARAMETER of
 * the builder, named at the call site:
 *   - `poOrderColumn({ orderSortField })` — the `Order` cycle state's row key:
 *     `id` on an order row, `order_number` on a hire row (a hire row's `id` is
 *     not the order's).
 *   - `requesterColumn({ key, label, width, min })` — "Ordered By" on the order
 *     tables, "Requested by" on the hires Table. Different LABELS, so different
 *     measured floors; the row key (`requested_by`) is the shared part.
 *   - `statusColumn({ sortField, width, min })` — `status` (alphabetical) on
 *     orders, `hire_status` (RANKED, `HIRE_STATUS_SORT_RANK`) on hires; the
 *     hire cell carries a countdown line under the badge, hence its own widths.
 *   - `supplierColumn({ width })` — the default width is a display preference;
 *     the `min` is the measured "Supplier" heading floor and is SHARED.
 *     🔀 2026-09-29 (owner's staging feedback): ~20% narrower at EACH call
 *     site — Orders 170 → 140, Hires 150 → 120; the 100 floor is untouched.
 *     🔀 That raised the hires Supplier `min` 90 → 100 on 2026-09-28: 90 was
 *     below the 94.13px the same heading measures in the same `SortableHeader`
 *     (`OrderTableView.js`'s min block), so a drag to 90 spilled the heading.
 *
 * 🪤 **A `min` is a property of the LABEL** (`OrderTableView.js`: "the floor is
 * the COLUMN'S OWN HEADING"), so a builder whose label is fixed owns its `min`,
 * and one whose label is a parameter takes the `min` as a parameter too.
 *
 * The parity ratchet (`components/tableParity.test.js`) recognises these
 * columns through `sharedColumnId(column)`, so a hires column rebuilt as a
 * literal stops counting as "present" and goes red.
 *
 * Column shape: `ui/PageScrollTable`'s docblock.
 */

// Which builder made a column. A WeakMap rather than a property, so the column
// objects keep exactly the shape every existing consumer and test reads.
const SHARED_ID = new WeakMap();
const tag = (id, column) => {
  SHARED_ID.set(column, id);
  return column;
};

/** The builder id that produced `column`, or `null` for a page-local literal. */
export const sharedColumnId = (column) => SHARED_ID.get(column) || null;

/**
 * The chevron column. `fixed: true` — a pure control column `Fit columns to
 * screen` never resizes. No heading text, so no measured floor: its `min` is
 * its own shipped width.
 */
export const expandColumn = () => tag('expand', {
  key: 'expand', label: '', width: 40, min: 40, align: 'left', fixed: true,
});

/**
 * PO/Order — the stacked PO-over-order-number cell with the cycling
 * `PO` → `Order` header. `sortField` stays `po_number` so a single-field
 * caller resolves to the resting `PO` state.
 *
 * 🔀 Width 90 / min 80 → **120 / 120 (owner's staging feedback, 2026-09-29)**:
 * the column must show "ORD-1234" plus BOTH the paperwork and paperclip
 * markers with no truncation, on Dept Orders and Dept Hires. The binding
 * constraint moved from the HEADING (`Order`, 75.81px of cell — the old 80)
 * to that DATA line. MEASURED in Chromium against the CI=true build's
 * stylesheet with Inter loaded: the order-number button holding "ORD-1234"
 * + two `ml-1` 14px markers is **95.48px** at `text-xs font-semibold` (Dept
 * Orders) and **94.96px** at `text-xs font-medium` (Dept Hires); + `px-2`
 * (16px) = **111.48px** of cell, rounded UP to the next ten = 120. Width
 * equals min so the default never starts below the floor. (`PO-0012` on the
 * top line is 57.99px at `text-sm` — well inside.)
 */
export const poOrderColumn = ({ orderSortField }) => tag('po_order', {
  key: 'po_number',
  label: 'PO',
  width: 120,
  min: 120,
  align: 'left',
  fitFloor: true,
  sortField: 'po_number',
  fields: [
    { sortField: 'po_number', label: 'PO', direction: 'asc' },
    { sortField: orderSortField, label: 'Order', direction: 'desc' },
  ],
});

/**
 * THE phone PO/Order column width — ONE value for every phone table (My
 * Orders, Dept Orders, To Approve, Dept Hires, My Hires), the width half of
 * `PhonePoOrderCell` (owner 2026-10-03: *"unify"*). Sized for the widest real
 * content, "ORD-1234" + paperwork + paperclip markers + the chevron, inside
 * the cell's `pl-1 pr-0.5` (measured at 375 in Chromium); `min` is the
 * heading floor ("PO" + caret).
 */
// 📏 100 (owner 2026-11-01: "reduce the padding on the left", so "No job name"
// fits the Job column; was 104 from 2026-10-03: "same width, small as possible,
// 4-digit order number and paperclip"): pl 2 + chevron 16 + gap 2 + "ORD-9999"
// 62.00 (Inter 600 12px, measured) + ml 2 + clip 14 + pr 0 = 98.00 → 100. A cell
// also carrying the delivery-paperwork marker ellipses — accepted.
export const PHONE_PO_ORDER_COLUMN = Object.freeze({ width: 100, min: 92 });

/** Who raised it. Row key `requested_by` on both tables — never the column key. */
export const requesterColumn = ({ key, label, width, min }) => tag('requester', {
  key, label, width, min, align: 'left', sortField: 'requested_by',
});

/** Supplier. Row key `supplier_name`; `min` 100 = "Supplier" 94.13px, measured. */
export const supplierColumn = ({ width }) => tag('supplier', {
  key: 'supplier', label: 'Supplier', width, min: 100, align: 'left', sortField: 'supplier_name',
});

/** Status. A badge — KNOWN width, so `fitFloor`. */
export const statusColumn = ({ sortField, width, min }) => tag('status', {
  key: 'status', label: 'Status', width, min, align: 'left', sortField, fitFloor: true,
});
